import type { Module } from 'vuex';
import { Setting } from '@/types/Setting';
import {RootState} from "@/store";

export interface SettingsState {
  settings: Setting[];
  possibleSettings: Setting[];
}

const settingsModule: Module<SettingsState, RootState> = {
  namespaced: true,

  state: (): SettingsState => ({
    settings: [],
    possibleSettings: [],
  }),

  getters: {
    getById: (state) => (idSetting: number): Setting | undefined => {
      return state.settings.find(setting => setting.idSetting === idSetting);
    },
    filteredSettings: (state): Setting[] => {
      const ret: Setting[] = [...state.settings];
      let index = -1;
      state.possibleSettings.forEach(possible => {
        const exists = ret.find(item => item.setting === possible.setting);
        if (!exists) {
          ret.push(new Setting(
            index,
            possible.setting,
            possible.name,
            possible.type,
            possible.value,
            true,
          ));
          index--;
        }
      });
      return ret;
    },
  },

  mutations: {
    setSettings(
      state,
      payload: { settings: Record<number, any>; possibleSettings?: Record<number, any> }
    ) {
      state.settings = Object
        .values(payload.settings)
        .map(raw => Setting.fromRaw(raw));
      if (payload.possibleSettings) {
        state.possibleSettings = Object.values(payload.possibleSettings).map((raw: any) =>
          new Setting(
            0,
            String(raw.setting),
            String(raw.name),
            String(raw.type),
            String(raw.value),
            true
          )
        );
      }
    },
    setPossibleSettings(
      state,
      payload: { possibleSettings: Record<number, any> }
    ) {
      state.possibleSettings = Object.values(payload.possibleSettings).map((raw: any) =>
        new Setting(
          0,
          String(raw.setting),
          String(raw.name),
          String(raw.type),
          String(raw.value),
          true
        )
      );
    },
    setSettingCreate(
      state,
      payload: {
        idSetting: number;
        setting: string;
        name: string;
        type: string;
        value: string;
      }
    ) {
      const raw = {
        idSetting: payload.idSetting,
        setting: payload.setting,
        name: payload.name,
        type: payload.type,
        value: payload.value,
        orphan: false,
      };
      state.settings.push(Setting.fromRaw(raw));
    },

    setSettingUpdate(
      state,
      payload: {
        idSetting: number;
        value?: string;
      }
    ) {
      const setting = state.settings.find(setting => setting.idSetting === payload.idSetting);
      if (!setting) return;
      if (payload.value !== undefined) {
        const updatedSetting = new Setting(
          setting.idSetting,
          setting.setting,
          setting.name,
          setting.type,
          payload.value,
          setting.orphan,
        );
        const index = state.settings.findIndex(s => s.idSetting === payload.idSetting);
        if (index !== -1) {
          state.settings[index] = updatedSetting;
        }
      }
    },

    setSettingDelete(
      state,
      payload: { idSetting: number }
    ) {
      state.settings = state.settings.filter(setting => setting.idSetting !== payload.idSetting);
    },
  },

  actions: {
    wsSetSettings({ commit }, payload) {
      commit('setSettings', payload);
    },
    wsSettingCreate({ commit }, payload) {
      commit('setSettingCreate', payload);
    },
    wsSettingUpdate({ commit }, payload) {
      commit('setSettingUpdate', payload);
    },
    wsSettingDelete({ commit }, payload) {
      commit('setSettingDelete', payload);
    },
    wsSetPossibleSettings({ commit }, payload) {
      commit('setPossibleSettings', payload);
    },
  },
};

export default settingsModule; 